import { Button, Card, CardActions, CardContent, CardHeader, Icon, Tooltip, Typography } from "@mui/material";
import { APICallStatus, Unit, VisitorParkingRequest, VisitorParkingRequestStatus } from "../../Types/UnitFeatureTypes";
import CalendarWithDate from '../CommonComponents/CalenderWithDate';
import { useNavigate } from "react-router-dom";
import { Cancel, CheckCircle, HourglassEmpty } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { approveVisitorParkingAPIStatus, currentUnit, statusUpdateVisitorParkingAPIStatus } from "../../Redux/selectors";
import { Actions } from "../../Redux/Reducer/rootReducer";
import { useAuth } from "../../context/authContext";
import { useEffect } from "react";
import { useApp } from "../../context/appContext";

const DisplayVisitorParkingCard: React.FC<{ item: VisitorParkingRequest }> = ({ item }) => {
    const navigate = useNavigate();
    const userUnitData = useSelector(currentUnit) as Unit;
    const dispatch = useDispatch();
    const {user} = useAuth();

    const approveStatus = useSelector(approveVisitorParkingAPIStatus);
    const statusUpdate = useSelector(statusUpdateVisitorParkingAPIStatus);
    const { setSnackbar } = useApp();

    useEffect(() => {
        if (approveStatus === APICallStatus.SUCCESS || statusUpdate === APICallStatus.SUCCESS) {
            setSnackbar(true, 'Visitor parking updated successfully', 'success');
            dispatch(Actions.fetchVisitorParking.visitorParkingActions.request({}));
            
        } else if (approveStatus === APICallStatus.FAILURE || statusUpdate === APICallStatus.FAILURE) {
            setSnackbar(true, 'Failed to update visitor parking request', 'error');
        }
        dispatch(Actions.visitorParking.resetStatusRequestAction());
    }, [approveStatus, statusUpdate])

    const handleParkingReschedule = (item: VisitorParkingRequest) => {
        navigate('/requestVisitorParking', { state: { 
            reschedule: true,
            id: item.id,
            unit_id: userUnitData.id,
            carBrand: item.car.make, 
            carModel: item.car.model, 
            carColor: item.car.color, 
            licensePlate: item.car.plate, 
            toDate: item.date_requested.end, 
            fromDate: item.date_requested.start
          }});
    };

    const getStatusIcon = (status: VisitorParkingRequestStatus, isInPast: boolean) => {
        let color: "success" | "error" | "warning" = 'success';
        let title = 'Approved';
        let icon = <CheckCircle />;

        if (status === VisitorParkingRequestStatus.REJECTED) {
            color = 'error';
            title = 'Rejected';
            icon = <Cancel />;
        } else if (status === VisitorParkingRequestStatus.CANCELLED) {
            color = 'error';
            title = 'Cancelled';
            icon = <Cancel />;
        } else
       if (status === VisitorParkingRequestStatus.APPROVED) {
            color = 'success';
            title = 'Approved';
            icon = <CheckCircle />;
        } else if (status === VisitorParkingRequestStatus.PENDING) {
            color = 'warning';
            title = 'Pending';
            icon = <HourglassEmpty />;
        } else {
            color = 'error';
            title = 'Not Approved';
            icon = <Cancel />;
        }

        return <Tooltip title={title}>
            <Icon sx={{mt:3, mr:1}} color={color as "success" | "error" | "warning"}>{icon}</Icon>
        </Tooltip>
    };

    const isInPast = new Date(item.date_requested.end) < new Date();

    if (isInPast) {
        return <></>
    }

    const isApproved = item.status === VisitorParkingRequestStatus.APPROVED;

    const handleApproveParking = (id?: number) => {
        if (!id) return;
        dispatch(Actions.visitorParking.approveVisitorParkingRequestAction.request({
            approval_date: new Date(),
            approved_by: user.id,
            id
        }));
    }

    const handleRejectParking = (id?: number) => {
        if (!id) return;
        dispatch(Actions.visitorParking.statusUpdateVisitorParkingRequestAction.request({
            id,
            status: VisitorParkingRequestStatus.REJECTED
        }));
    }

    const handleCancelParking = (id?: number) => {
        if (!id) return;
        dispatch(Actions.visitorParking.statusUpdateVisitorParkingRequestAction.request({
            id,
            status: VisitorParkingRequestStatus.CANCELLED
        }));
    }

    return (
        <Card sx={{ borderRadius: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', backgroundColor: isInPast || item.status === VisitorParkingRequestStatus.REJECTED || item.status === VisitorParkingRequestStatus.CANCELLED ? 'lightgrey' : '' }}>
            <CardHeader title={`${item.car.make} ${item.car.model}`} subheader={item.car.plate}
                action={getStatusIcon(item.status, isInPast)}
            />
            <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                <Tooltip title={`${item.car.make} ${item.car.model} ${item.car.color}`}>
                    <Typography variant="h6" color="textSecondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
                        {item.car.make} {item.car.model} {item.car.color}
                    </Typography>
                </Tooltip>
            </CardContent>
            <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', minHeight: 185, minWidth: 230 }}>
                <CalendarWithDate
                    start={new Date(item.date_requested.start)}
                    end={new Date(item.date_requested.end)}
                />
            </CardContent>
            {item.status === VisitorParkingRequestStatus.PENDING && !isInPast && <CardActions sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button variant="contained" color="primary" sx={{
                    '&:hover': {
                        backgroundColor: 'green'
                    }
                }} onClick={() => handleApproveParking(item.id)}>
                    Approve
                </Button>
                <Button variant="contained" color="secondary" sx={{
                    '&:hover': {
                        backgroundColor: 'red'
                    }
                }} onClick={() => handleRejectParking(item.id)}>
                    Reject
                </Button>
            </CardActions>}
            {isApproved && !isInPast && <CardActions sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button variant="contained" color="primary" onClick={() => handleParkingReschedule(item)}>
                    Reschedule
                </Button>
                <Button variant="contained" color="secondary" sx={{
                    '&:hover': {
                        backgroundColor: 'red'
                    }
                }} onClick={() => handleCancelParking(item.id)}>
                    Cancel
                </Button>
            </CardActions>}
        </Card>
    );
}

export default DisplayVisitorParkingCard;